<script lang="ts">
	import type { RestaurantDynamic, RestaurantsDynamic, RestaurantSemiStatic, RestaurantsSemiStatic, TileRestaurant, TileStack as TileRestaurants } from "$lib/def/landing";
	import type { Cache } from '$lib/serv/core/cache';
	import { KEY_PRIMARY_FILTER, KEY_SECONDARY_FILTER, sort, type DealsLoader } from "$lib/serv/food-landing";

	import { browser } from "$app/environment";
	import { Vertical } from "$lib/def/verticals";
	import { queryFrom } from "$lib/util/url";
	import { BehaviorSubject, combineLatest, tap, map, startWith, Subject, switchMap, type Observable, type Subscription, firstValueFrom, share, shareReplay } from "rxjs";
	import { onDestroy } from "svelte";

	import Grid from "$lib/ui/layout/Grid.svelte";
	import Tile from "./Tile.svelte";
	import TileStack, { KEY_GROUP } from "./TileStack.svelte";
import { get$ } from "$lib/serv/core/auth";

	//#####################################################################################
	//
	// This component's purpose in order of priority:
	// 
	// 1. Take the semistatic restaurant data and render a grid of Tile components.
	// 2. Take the dynamic data and (combined with the semistatic data) use it's layout to
	// render a grid of Tile and TileStack components.
	// 3. Take a list of restaurant IDs (`filtered`) and render a grid of Tile components.
	// 4. Take a stack/group ID and focus on a TileStack component.
	// 
	//#####################################################################################
	
	/**
	 * The semistatic data returned from the server.
	 */
	export let semistatic: Cache<RestaurantsSemiStatic> | null;
	
	/**
	 * The dynamic data returned from the server.
	 */
	export let dynamic: Cache<RestaurantsDynamic> | null;

	/**
	 * Data containing restaurant promotions, deals and marketing labels.
	 */
	export let deals: DealsLoader | null;
	
	/**
	 * The filtered data returned from the server. Ignored if `filteredList` is set.
	 */
	export let filtered$: Observable<Cache<number[]> | null> | null = null;

	/**
	 * A list of restaurant IDs filtering the output. If set, `filtered$` is ignored and
	 * `stack` will have no effect.
	 */
	export let filteredList: number[] | null = null;

	/**
	 * A group (stack) to focus on. This will hide all other Tile and TileStack components
	 * and allow a single TileStack to flatten it's cards. *NOTE:* Not to be used with
	 * `filteredList`.
	 */
	export let group: string | null = null;

	/**
	 * Current URL search params.
	 */
	export let searchParams: Record<string, string>;

	type Output = {
		/**
		 * Is semistatic data loading?
		 */
		loadingSS?: boolean,

		/**
		 * Is dynamic data loading?
		 */
		loadingDyn?: boolean,

		/**
		 * Is filtered data loading?
		 */
		loadingFiltered?: boolean,

		/**
		 * Semistatic restaurant data.
		 */
		semistatic: RestaurantSemiStatic[],

		/**
		 * Semistatic restaurant data.
		 */
		dynamic: RestaurantDynamic[],

		/**
		 * Fallback tiles if `tiles` is not yet loaded.
		 */
		tilesFlat: (TileRestaurant | TileRestaurants)[],

		/**
		 * Fallback tiles if `tiles` is not yet loaded.
		 */
		tiles?: (TileRestaurant | TileRestaurants)[],

		/**
		 * Override tiles for filtered results.
		 */
		tilesFiltered?: (TileRestaurant | TileRestaurants)[],

		/**
		 * Any error during semistatic loading or filtered loading. These are more sereve than dynamic loading error info.
		 */
		error?: any,

		/**
		 * Any error during dynamic loading.
		 */
		errorDyn?: any,
	}

	let subSemiStatic: Subscription | undefined;
	let subDynamic: Subscription | undefined;
	let subFiltered: Subscription;

	let subjSemiStatic = new Subject<Cache<RestaurantsSemiStatic> | null>();
	let subjDynamic = new Subject<Cache<RestaurantsDynamic> | null>();
	let subjFiltered = new BehaviorSubject<Cache<number[]> | null>(null);
	let subjFilteredList = new BehaviorSubject<number[] | null>(null);

	let output$:Observable<Output> = subjSemiStatic.pipe(
		map(cache => ({
			loadingSS: cache?.loading,
			semistatic: cache?.payload?.semi_static ?? [],
			tilesFlat: cache?.payload?.semi_static ? tilesFromSemiStatic(cache?.payload.semi_static) : [],
			error: cache?.error,
		})),
		switchMap(output => subjDynamic.pipe(
			map(cache => ({
				...output,
				loadingDyn: cache?.loading,
				dynamic: cache?.payload?.dynamic ?? [],
				tiles: cache?.payload?.tiles,
				errorDyn: cache?.error,
			})),
			startWith({ ...output, dynamic: [] as RestaurantDynamic[] }),
		)),
		switchMap(output => combineLatest([subjFiltered, subjFilteredList]).pipe(
			map(([cache, list]) => {
				if (!cache && !list) return { ...output };
				if (list) {
					cache = { loading: false, payload: list, type: 'fresh'} as Cache<number[]>;
				}

				// TM: The commented-out `return` below is unreachable; however, it keeps TS happy
				// (it infers correctly for the final `return` that `cache` is not null at that point)
				if (!cache) return { ...output };

				return {
					...output,
					loadingFiltered: cache.loading,
					tilesFiltered: cache?.payload
						? tilesFromSemiStatic(sort(cache.payload, "dynamic", dynamic?.payload))
						: [],
					error: output.error || cache.error,
				}
			}),
		)),
		shareReplay(1), // workaround for Svelte 5 late template subscription
	);

	$: {
		subjSemiStatic.next(semistatic);
	}

	$: {
		subjDynamic.next(dynamic);
	}

	$: {
		if (browser) {
			subFiltered?.unsubscribe();
			if (filtered$) subFiltered = filtered$.subscribe({
				next: (value) => subjFiltered.next(value)
			});
		}
	}
	$: {
		if (browser) {
			subjFilteredList.next(filteredList)
		}
	}

	function tilesFromSemiStatic(ss:({id:number} | number)[]) {
		return ss?.map(r => ({
			payload: {
				id: typeof r === 'number' ? r : r.id
			}
		} as TileRestaurant));
	}

	const getVerticalFromUrl = (params: Record<string, string>) => params['vertical'] as Vertical || Vertical.Restaurant;

	async function loadGrp(id: number | string | null) {
		if (!id) return '';
		const {name} = await firstValueFrom(get$(`api://exposure/restaurantgroups/${id}`)) as {name:string};
		return name;
	}

	const subOutput = output$.subscribe();  // workaround for Svelte 5 late template subscription
	onDestroy(() => {
		subSemiStatic?.unsubscribe();
		subDynamic?.unsubscribe();
		subFiltered?.unsubscribe();
		subOutput.unsubscribe();
	});
</script>

{#if $output$}
	{@const output = $output$}
	{#if output.loadingSS || output.loadingFiltered}
		<!-- <h2 class="headline-m bold loading">&nbsp;</h2>
		<div class="grid restaurants loading">
			{#each Array(10) as _}
				<a href={null} class="card"><div class="image"></div><div class="text"></div></a>
			{/each}
		</div> -->
		<Grid cache={{ loading: true }} type="restaurants">
			<h3 slot="title" class="headline-m bold">&nbsp;</h3>
			<div class="item card">
				<div class="image">
					<div class="imgInner"></div>
				</div>
				<div class="text">
					<h5 class="label-xl medium">Restaurant</h5>
				</div>
			</div>
		</Grid>
	{:else if output.error}
		<div class="message error body-m book">
			{output.error.error?.friendly_message ?? output.error}
		</div>
	{:else}
		{#if output.tilesFiltered && !output.tilesFiltered.length}
			{@const primary = decodeURIComponent(searchParams[KEY_PRIMARY_FILTER] ?? '')}
			{@const secondary = decodeURIComponent(searchParams[KEY_SECONDARY_FILTER] ?? '').split(',')}
			<div class="results-message">
					<div class="display-s book">No results near you</div>
					<div class="body-m book medium-importance">Try adjusting your filters</div>
					<div class="pills">
						{#if primary}
							<a href={`/?${queryFrom(searchParams).remove(KEY_PRIMARY_FILTER)}`} class="background-elevation-3dp label-xl bold"><span class="icon-close-14"></span>{primary}</a>
						{/if}
						{#if secondary}
							{#each secondary as s}
								{#if s}
									<a href={`/?${queryFrom(searchParams).subtoggle(KEY_SECONDARY_FILTER, encodeURIComponent(s))}`} class="background-elevation-3dp label-xl bold"><span class="icon-close-16"></span>{s}</a>
								{/if}
							{/each}
						{/if}
					</div>
					<!-- <a href={`/${clearUrl($page.url.searchParams)}`} class="button body-m bold">Clear all filters</a> -->
			</div>
		{:else}
			{@const tiles = (
				output.tilesFiltered
				?? output.tiles
				?? output.tilesFlat
			).filter((tile) => {
				if (tile.type === "stack") {
					return true;
				}

				// Only show tiles with semistatic data available (putting it here so that the `count` label is correct)
				const id = tile.payload.id;
				const ss = semistatic?.payload?.semi_static?.find(r => r.id === id);
				return !!ss;
			})}
			{@const count = tiles.reduce((prev, cur) => cur.type === 'stack' ? prev + cur.payload.restaurant_ids.length : prev + 1, 0)}
			{@const vertical = searchParams['vertical']}

			{#if tiles?.length}
				{@const groupNotFound = group && !tiles.find(({type, payload}) => type === 'stack' && +(group??'') === payload.group_id)}
				{#if group && groupNotFound}
					<div class="body-m book">
						{#await loadGrp(group)}
							...
						{:then name}
							{name ?? 'This merchant'} is not available in your area.
						{:catch error}
							{error}.
						{/await}
					</div>
					<a href={`/?${queryFrom(searchParams).remove(KEY_GROUP)}`}>
						View all {vertical}s near you.
					</a>
				{/if}
				<div class="m-v-l">
					{#if !group}
						<h2 class="headline-m bold">
							{
							(count === output.tilesFlat?.length ? 'All ' : '') +
							count +
							(vertical ? ` ${vertical + (count > 1 ? 's' : '')}` : '')
							}
							near you
						</h2>
					{/if}

					<div class="restaurants" class:grid={!group} data-testid="grid-restaurants">
						{#each tiles as tile}
							{#if tile.type === 'stack' && (!group || +group === tile.payload.group_id)}
								<TileStack {searchParams} data={tile} restaurants={output.semistatic} dynamic={output.dynamic} expanded={!!group} {deals} />
							{:else if !group && tile.type !== 'stack'}
								{@const id = tile.payload.id}
								{@const ss = semistatic?.payload?.semi_static?.find(r => r.id === id)}
								{@const d = dynamic?.payload?.dynamic?.find(r => r.id === id)}
								{@const ds = deals?.restaurants?.get(id)}
								<Tile {searchParams} tile={{ ...tile, type: getVerticalFromUrl(searchParams)}} semistatic={ss} dynamic={d} deals={ds}/>
							{/if}
						{/each}
					</div>
				</div>
			{:else}
				<div class="message error body-m book">
					<h4 class="headline-s">We have a problem</h4>
					<p class="body-m book">We can't find any restaurants at the moment.</p>
				</div>
			{/if}
		{/if}
	{/if}
{/if}